import type { PropsElement } from '../props';
import { areaOf, isType } from '../props';
import type { ToolFn, ToolSpec } from '../types';
import { inStorey, ofType, readProp, round } from './select';

export const findSpec: ToolSpec = {
  name: 'find_elements',
  description:
    'Find elements by IFC type with an optional property filter. Returns id, name, storey, material and the requested property. Use op "missing" to find elements where the property is not set. Rooms also return room name, number and area.',
  input_schema: {
    type: 'object',
    properties: {
      type: { type: 'string' },
      storey: { type: 'string' },
      pset: {
        type: 'string',
        description: 'Property set name, e.g. Pset_DoorCommon, PSet_Revit_Dimensions',
      },
      prop: { type: 'string', description: 'Property name, e.g. FireRating, Area, LoadBearing' },
      op: { type: 'string', enum: ['eq', 'gt', 'lt', 'missing', 'set'] },
      value: { type: 'string' },
      limit: { type: 'integer' },
    },
    required: ['type'],
  },
};

export const find =
  (elements: PropsElement[]): ToolFn =>
  ({ type, storey, pset, prop, op, value, limit }) => {
    let hits = inStorey(ofType(elements, type), storey);
    if (pset && prop) {
      const want = String(value ?? '').toLowerCase();
      hits = hits.filter((e) => {
        const v = readProp(e, pset, prop);
        switch (op ?? 'set') {
          case 'missing':
            return v == null;
          case 'set':
            return v != null;
          case 'eq':
            return v != null && v.toLowerCase() === want;
          case 'gt':
            return v != null && Number(v) > Number(value);
          case 'lt':
            return v != null && Number(v) < Number(value);
          default:
            return true;
        }
      });
    }
    const max = Math.min(Number(limit) || 40, 80);
    return {
      count: hits.length,
      elements: hits.slice(0, max).map((e) => summary(e, pset, prop)),
      ids: hits.map((e) => e.expressID),
    };
  };

function summary(e: PropsElement, pset: unknown, prop: unknown) {
  const area = areaOf(e);
  return {
    id: e.expressID,
    type: e.type,
    name: e.name.slice(0, 60),
    storey: e.storey,
    ...(e.material ? { material: e.material.names.join(' | ') } : {}),
    ...(pset && prop ? { [String(prop)]: readProp(e, pset, prop) } : {}),
    ...(isType(e, 'IfcSpace')
      ? {
          room: readProp(e, 'PSet_Revit_Identity Data', 'Name') ?? e.name,
          number: readProp(e, 'PSet_Revit_Identity Data', 'Number'),
          area_m2: area ? round(area.value) : null,
        }
      : {}),
  };
}
